import type { AuthType } from '../types/auth';
import { SessionChangedError } from './refreshFailure';

/**
 * Cada operación sale con la sesión con la que EMPEZÓ (spec 2026-10-01 § 12.11).
 *
 * Una operación puede pasar varias veces por `authMiddleware`: el reintento
 * tras una 401, los del `RetryLink` (hasta 5, con espera) y los de los códigos
 * transitorios del backend. Cada paso lee la sesión ACTUAL. Si entre medias
 * alguien cierra sesión y entra otro (tablet compartida) o cambia de tenant,
 * la escritura de A saldría con el token de B, a nombre de B y a su tenant.
 *
 * Por eso la sesión se fija en el PRIMER paso y los siguientes la exigen. Y
 * una 401 de A que llega tarde no refresca la sesión de B.
 */

/** Sin sesión también es una sesión: lo que empezó sin token, sigue sin él. */
const NO_SESSION = '';

/**
 * Identidad de la sesión. El refresh token cambia al entrar y al cambiar de
 * tenant, y NO al refrescar el access token (el back solo devuelve este).
 */
export function sessionKeyOf(session: AuthType | null | undefined): string {
  if (!session?.refreshToken) return NO_SESSION;
  return `${session.user?.id ?? ''}|${session.user?.activeTenantId ?? ''}|${session.refreshToken}`;
}

/** Lo que `authMiddleware` deja en el contexto de la operación. */
export interface SessionPinContext {
  /** Sesión con la que salió la operación la primera vez. */
  sessionKey?: string;
  /** Access token con el que salió la última vez. */
  sentAccessToken?: string;
}

/**
 * Para `authMiddleware`: fija la sesión en el primer paso; en los siguientes
 * exige la misma o lanza `SessionChangedError` (no se envía nada).
 */
export function pinSession(
  previous: SessionPinContext,
  session: AuthType | null | undefined,
): Required<SessionPinContext> {
  const sessionKey = sessionKeyOf(session);
  if (previous.sessionKey !== undefined && previous.sessionKey !== sessionKey) {
    throw new SessionChangedError();
  }
  return { sessionKey, sentAccessToken: session?.accessToken ?? '' };
}

export type UnauthenticatedAction = 'session-changed' | 'retry' | 'refresh';

/**
 * Para la 401: qué hacer sin tocar la sesión de otro.
 *
 * - La sesión ya no es la de la operación → `session-changed`: ni se refresca
 *   (sería refrescar la sesión de B por una 401 de A) ni se repite.
 * - Es la misma, pero su access token ya cambió desde que salió (otra consulta
 *   refrescó antes) → `retry` con el actual, sin otro refresco.
 * - Si no → `refresh`, como siempre.
 */
export function onUnauthenticated(
  context: SessionPinContext,
  current: AuthType | null | undefined,
): UnauthenticatedAction {
  if (context.sessionKey !== undefined && context.sessionKey !== sessionKeyOf(current)) {
    return 'session-changed';
  }
  if (
    context.sentAccessToken &&
    current?.accessToken &&
    context.sentAccessToken !== current.accessToken
  ) {
    return 'retry';
  }
  return 'refresh';
}
